import { createAsyncTypes } from 'utils/ActionCreator'
import { filterEmailEssentialsByPax8Hierarchy } from 'utils/plans'

import { PLAN_TYPES } from 'containers/App/constants'
import { EMAIL_DLP_ADDON_NAME, PLAN_OPTIONS } from 'containers/Partner/EditTenant/constants'

// Action type namespace for ConnectWise integration
const CONNECTWISE_ACTION_NAMESPACE = 'Settings/MalwareDetection/ConnectWise'

export const WIZARD_STEPS = Object.freeze({
  CONNECTION: 1,
  COMPANY_MAPPING: 2,
  PRODUCT_MAPPING: 3,
  TICKET_BOARD: 4,
  REVIEW: 5,
})

export const STEP_TITLES = [
  'Connection',
  'Company & Agreement mapping',
  'Product Mapping',
  'Ticket Board',
  'Review',
]

// Map frontend product labels to backend plan names
export const PLAN_NAME_MAPPING = {
  Core: 'core',
  HRM: 'sat_bundle_plus',
  'Human Risk Management': 'sat_bundle_plus',
  'SAT Suite': 'sat_bundle_plus',
  'Email Protect': 'email_protect',
  'Email Protect VAR': 'email_protect_var',
  'Email Protect MSP': 'email_protect_var',
  'IRONSCALES Protect': 'ironscales_protect',
  'Complete Protect': 'complete_protect',
  ATO: 'ato',
  DMARC: 'dmarc',
  'SAT Bundle Plus': 'sat_bundle_plus', // SAT Bundle Plus is an addon that also maps to sat_bundle_plus
  'Incident Management': 'incident_management',
  'Email Encryption': 'email_encryption',
}

// Async action types using ActionCreator
export const TEST_CONNECTION_ASYNC = createAsyncTypes(
  'TEST_CONNECTION',
  CONNECTWISE_ACTION_NAMESPACE
)

export const GET_COMPANIES_ASYNC = createAsyncTypes('GET_COMPANIES', CONNECTWISE_ACTION_NAMESPACE)

export const SEARCH_COMPANIES_ASYNC = createAsyncTypes(
  'SEARCH_COMPANIES',
  CONNECTWISE_ACTION_NAMESPACE
)
// Distinct stream for infinite scroll — uses mergeMap so concurrent page fetches
// don't cancel each other (switchMap on SEARCH would drop intermediate pages).
export const LOAD_MORE_COMPANIES_ASYNC = createAsyncTypes(
  'LOAD_MORE_COMPANIES',
  CONNECTWISE_ACTION_NAMESPACE
)
export const COMPANIES_SEARCH_PAGE_SIZE = 25

export const GET_AGREEMENTS_ASYNC = createAsyncTypes('GET_AGREEMENTS', CONNECTWISE_ACTION_NAMESPACE)

export const GET_PRODUCTS_ASYNC = createAsyncTypes('GET_PRODUCTS', CONNECTWISE_ACTION_NAMESPACE)

export const GET_SERVICE_BOARDS_ASYNC = createAsyncTypes(
  'GET_SERVICE_BOARDS',
  CONNECTWISE_ACTION_NAMESPACE
)

export const GET_BOARD_STATUSES_ASYNC = createAsyncTypes(
  'GET_BOARD_STATUSES',
  CONNECTWISE_ACTION_NAMESPACE
)

export const GET_PRIORITIES_ASYNC = createAsyncTypes('GET_PRIORITIES', CONNECTWISE_ACTION_NAMESPACE)

export const GET_ALL_IRONSCALES_COMPANIES_ASYNC = createAsyncTypes(
  'GET_ALL_IRONSCALES_COMPANIES',
  CONNECTWISE_ACTION_NAMESPACE
)

export const GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC = createAsyncTypes(
  'GET_UNMAPPED_IRONSCALES_COMPANIES',
  CONNECTWISE_ACTION_NAMESPACE
)

export const GET_INTEGRATION_ASYNC = createAsyncTypes(
  'GET_INTEGRATION',
  CONNECTWISE_ACTION_NAMESPACE
)

export const SAVE_INTEGRATION_ASYNC = createAsyncTypes(
  'SAVE_INTEGRATION',
  CONNECTWISE_ACTION_NAMESPACE
)

export const UPDATE_FORM_FIELD = 'Settings/MalwareDetection/ConnectWise/UPDATE_FORM_FIELD'
export const SET_CURRENT_STEP = 'Settings/MalwareDetection/ConnectWise/SET_CURRENT_STEP'
export const RESET_FORM = 'Settings/MalwareDetection/ConnectWise/RESET_FORM'
export const CLEAR_TEST_CONNECTION_STATE =
  'Settings/MalwareDetection/ConnectWise/CLEAR_TEST_CONNECTION_STATE'
export const SET_PAGE = 'Settings/MalwareDetection/ConnectWise/SET_PAGE'
export const SET_CONNECTION_EDIT_MODE =
  'Settings/MalwareDetection/ConnectWise/SET_CONNECTION_EDIT_MODE'

export const initialConnectionState = {
  connection_url: '',
  company_id: '',
  public_key: '',
  private_key: '',
  tested: false,
}

export const initialProductMappingState = [
  {
    id: 5,
    category: 'products',
    label: 'Human Risk Management',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 1,
    category: 'products',
    label: 'Core',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 2,
    category: 'products',
    label: 'Email Protect',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 3,
    category: 'products',
    label: 'IRONSCALES Protect',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 4,
    category: 'products',
    label: 'Complete Protect',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 6,
    category: 'addons',
    label: 'ATO',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 7,
    category: 'addons',
    label: 'DMARC',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 8,
    category: 'addons',
    label: 'SAT Bundle Plus',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 9,
    category: 'addons',
    label: 'Incident Management',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
  {
    id: 10,
    category: 'addons',
    label: 'Email Encryption',
    connectwiseProduct: '',
    connectwiseProductName: '',
    error: null,
  },
]

const PLAN_ID_TO_MAPPING = {
  [PLAN_TYPES.CORE]: 'core',
  [PLAN_TYPES.EMAIL_PROTECT]: 'email_protect',
  [PLAN_TYPES.EMAIL_ESSENTIALS]: 'email_essentials',
  [PLAN_TYPES.EMAIL_PROTECT_VAR]: 'email_protect_var',
  [PLAN_TYPES.EMAIL_PROTECT_PRO]: 'email_protect_pro',
  [PLAN_TYPES.COMPLETE_PROTECT]: 'complete_protect',
  [PLAN_TYPES.IRONSCALES_PROTECT]: 'ironscales_protect',
  [PLAN_TYPES.SAT_SUITE]: 'sat_bundle_plus',
  [PLAN_TYPES.CAMPAIGNS_ONLY]: 'incident_management',
}

const ADDON_NAME_TO_MAPPING = {
  ATO: 'ato',
  DMARC: 'dmarc',
  'DMARC Management': 'dmarc',
  'SAT Bundle Plus': 'sat_bundle_plus',
  'Incident Management': 'incident_management',
  'Email Encryption': 'email_encryption',
}

const normalizeToPlanSlug = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

const withDefaultMappingFields = (item) => ({
  ...item,
  connectwiseProduct: '',
  connectwiseProductName: '',
  error: null,
})

const getPlanLabelById = (planId) =>
  PLAN_OPTIONS.find((option) => Number(option.value) === Number(planId))?.label || `Plan ${planId}`

const shouldDisplayAddon = (addon) =>
  Boolean(addon?.name) &&
  (!addon.displayIfActive ||
    addon.currentState === true ||
    addon.activeByDefault === true ||
    addon.editable)

const buildDynamicTemplate = (partnerPayload, isPax8Hierarchy) => {
  const availablePlanIds = Array.isArray(partnerPayload?.availablePlans)
    ? partnerPayload.availablePlans
    : []
  const allowedPlanIds = new Set(availablePlanIds.map(Number).filter(Number.isInteger))
  const allowedOptionsByPillar = filterEmailEssentialsByPax8Hierarchy(
    PLAN_OPTIONS,
    isPax8Hierarchy === true
  )
  const allowedPlanIdsByPillar = new Set(
    allowedOptionsByPillar.map((option) => Number(option.value)).filter(Number.isInteger)
  )

  const dynamicProducts = Object.entries(PLAN_ID_TO_MAPPING)
    .filter(([planId]) => allowedPlanIdsByPillar.has(Number(planId)))
    .filter(([planId]) => allowedPlanIds.size === 0 || allowedPlanIds.has(Number(planId)))
    .map(([planId, ironscalesPlan]) => ({
      id: `products:${ironscalesPlan}`,
      category: 'products',
      label: getPlanLabelById(planId),
      ironscalesPlan,
    }))

  const dynamicAddonsBySlug = new Map()
  Object.values(partnerPayload?.addons || {}).forEach((addonsByPlan) => {
    Object.values(addonsByPlan || {})
      .filter(Boolean)
      .filter(shouldDisplayAddon)
      .filter((addon) => addon.name !== EMAIL_DLP_ADDON_NAME)
      .forEach((addon) => {
        const label = String(addon.name).trim()
        if (!label) return
        const ironscalesPlan = ADDON_NAME_TO_MAPPING[label] || normalizeToPlanSlug(label)
        if (dynamicAddonsBySlug.has(ironscalesPlan)) return
        dynamicAddonsBySlug.set(ironscalesPlan, {
          id: `addons:${ironscalesPlan}`,
          category: 'addons',
          label,
          ironscalesPlan,
        })
      })
  })

  const result = [...dynamicProducts, ...Array.from(dynamicAddonsBySlug.values())]
  if (!result.length) return initialProductMappingState
  return result.map(withDefaultMappingFields)
}

export const buildProductMappingsTemplateFromPartner = (partnerPayload, isPax8Hierarchy) =>
  buildDynamicTemplate(partnerPayload, isPax8Hierarchy)

export const initialTicketBoardState = {
  boards: [], // Array of { boardId, boardName, statuses: [{ statusId, statusName, priorityId }] }
  defaultBoardId: '', // Default board ID for backward compatibility
}

export const initialFormState = {
  currentStep: WIZARD_STEPS.CONNECTION,
  connection: initialConnectionState,
  allCompanies: [],
  unmappedCompanies: [],
  mappedCompanies: [],
  allCompaniesCount: 0,
  unmappedCompaniesCount: 0,
  productMappings: initialProductMappingState,
  ticketBoard: initialTicketBoardState,
  fieldErrors: {},
  testConnectionLoading: false,
  testConnectionError: null,
  testConnectionSuccess: false,
  isSubmitting: false,
  submitError: null,
  allCompanyLoader: false,
  unmappedCompanyLoader: false,
  page: 1,
  agreementsLoaders: [],
  isConnectionEditMode: false,
}

export const COMPANIES_PER_PAGE = 5

// Modal close delay in milliseconds (allows React to finish processing before closing)
export const MODAL_CLOSE_DELAY_MS = 100

// Error messages
export const MISSING_CONNECTION_CREDENTIALS_ERROR = 'Missing connection credentials'
